import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {ApiService} from '../../../core/http/api.service';
import {of} from 'rxjs';
import {CURRENT_USER_STORAGE_KEY} from '../../../core/constants/local-storage-keys.constansts';
import {
  GetCurrentUserDetailsResponseModel, GetUserPermissionResponseModel, GetUserUploadsResponseModel,
  LoadCurrentUserPermissionsModel, LoadCurrentUserUploadDetailsModel
} from '../../../shared/models/users/users.models';
import { CurrentUserActions } from '../../actions/user/current-user.actions';
import { AmplifyUserService } from '../../../core/services/amplify/user/amplify-user.service';

const fields = [
  "id",
  "guard",
  "title",
  "name",
  "email",
  "is-activated",
  "is-verified",
  "activated-at",
  "last-login",
  "house-name",
  "address1",
  "address2",
  "town",
  "county",
  "postcode",
  "phone",
  "phone-alt",
  "newsletter",
  "heard",
  "is-banned",
  "id-authorised",
  "timezone",
  "show-as-online",
  "last-seen",
  "about-me",
  "referrer",
  "secondary-contact-title",
  "secondary-contact-name",
  "secondary-contact-email",
  "secondary-contact-mobile",
  "secondary-contact-second-number",
  "secondary-contact-relationship",
  "salesforce-id",
  "lpe-bio",
  "lpe-region-id",
  "include-in-directory",
  "lpe-training-date",
  "user-title",
  "lpe-end-date",
  "lpe-start-date",
  "sub-territory",
  "disabled",
  "equipment-cost",
  "date-of-birth",
  "personal-email",
  "lpe-leaving-reason",
  "lpe-leaving-reason-text",
  "recruited-id",
  "recruited-by",
  "registered-hmrc",
  "registered-tpos",
  "company",
  "author-id",
  "created-by",
  "request-id",
  "salesforce-updated-at",
  "permissions",
  "guard-name",
  "created-at",
  "updated-at",
  'auto-confirm-enabled',
  'branch-agent-impersonate-enabled'
];

@Injectable()
export class CurrentUserEffects {
   loadCurrentUserDetails$ = createEffect(() => this.actions$
    .pipe(
      ofType(CurrentUserActions.loadCurrentUser),
      mergeMap(
        () => this.api.Users.getCurrentUserDetails({fields})
          .pipe(
            map((response: GetCurrentUserDetailsResponseModel) => CurrentUserActions.loadCurrentUserSuccess(response)),
            catchError(error => of(CurrentUserActions.loadCurrentUserFailure(error)))
          )
      )
    ));

   loadAmplifyUserDetails$ = createEffect(() => this.actions$
    .pipe(
      ofType(CurrentUserActions.loadAmplifyUser),
      mergeMap(
        () => this.amplifyUserService.getCurrentUserDetails()
          .pipe(
            map((response: GetCurrentUserDetailsResponseModel) => CurrentUserActions.loadAmplifyUserSuccess(response)),
            catchError(error => of(CurrentUserActions.loadAmplifyUserFailure(error)))
          )
      )
    ));

   loadCurrentUserPermissions$ = createEffect(() => this.actions$
    .pipe(
      ofType(CurrentUserActions.loadCurrentUserPermissions),
      mergeMap(
        (data: LoadCurrentUserPermissionsModel) => this.api.Users.getCurrentUserPermissions(data.userId)
          .pipe(
            map((response: GetUserPermissionResponseModel) => CurrentUserActions.loadCurrentUserPermissionsSuccess(response)),
            catchError(error => of(CurrentUserActions.loadCurrentUserPermissionsFailure(error)))
          )
      )
    ));

   loadAmplifyUserPermissions$ = createEffect(() => this.actions$
    .pipe(
      ofType(CurrentUserActions.loadAmplifyUserPermissions),
      mergeMap(
        (data: LoadCurrentUserPermissionsModel) => this.amplifyUserService.getUserPermissions(data.userId)
          .pipe(
            map((permissions: any) => CurrentUserActions.loadAmplifyUserPermissionsSuccess({data: permissions})),
            catchError(error => of(CurrentUserActions.loadAmplifyUserPermissionsFailure(error)))
          )
      )
    ));

   loadCurrentUserUploadDetails$ = createEffect(() => this.actions$
    .pipe(
      ofType(CurrentUserActions.loadCurrentUserUploadDetails),
      mergeMap(
        (data: LoadCurrentUserUploadDetailsModel) => this.api.Uploads.getUserUploads(data.userId, data.params)
          .pipe(
            map((response: GetUserUploadsResponseModel) => CurrentUserActions.loadCurrentUserUploadDetailsSuccess(response)),
            catchError(error => of(CurrentUserActions.loadCurrentUserUploadDetailsFailure(error)))
          )
      )
    ));

   loadCurrentUserFromStorage$ = createEffect(() => this.actions$
    .pipe(
      ofType(CurrentUserActions.loadCurrentUserFromStorage),
      map(() => {
        const userData = JSON.parse(localStorage.getItem(CURRENT_USER_STORAGE_KEY));
        return CurrentUserActions.loadCurrentUserFromStorageSuccess({data: userData});
      })
    ));


  constructor(
    private actions$: Actions,
    private api: ApiService,
    private amplifyUserService: AmplifyUserService
  ) {
  }
}
